import React, { useEffect } from "react";
import "./App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Navigation } from "./components/Navigation";
import { Hero } from "./components/Hero";
import { HeroSliderSection } from "./components/HeroSliderSection";
import { Events } from "./components/Events";
import { Contact } from "./components/Contact";
import { Footer } from "./components/Footer";
import { FloatingDots } from "./components/FloatingDots";
import { SectionDivider } from "./components/SectionDivider";
import { AboutPage } from "./pages/AboutPage";
import { ReleasesPage } from "./pages/ReleasesPage";
import { ArtistsPage } from "./pages/ArtistsPage";
import { ArtistDetailPage } from "./pages/ArtistDetailPage";
import { ReleaseDetailPage } from "./pages/ReleaseDetailPage";
import GalleryLanding from "./components/GalleryLanding";
import GalleryView from "./components/GalleryView";
import PartyGallery from "./components/PartyGallery";
import { Toaster } from "./components/ui/sonner";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { RadioProvider } from "./context/RadioContext";
import { AdminLogin } from "./components/admin/AdminLogin";
import { AdminLayout } from "./components/admin/AdminLayout";
import { Dashboard } from "./components/admin/Dashboard";
import { ReleasesManager } from "./components/admin/releases/ReleasesManager";
import { ArtistsManager } from "./components/admin/ArtistsManager";
import { EventsManager } from "./components/admin/EventsManager";
import { ContactsViewer } from "./components/admin/ContactsViewer";
import { ContactManager } from "./components/admin/ContactManager";
import { HeroSlidesManager } from "./components/admin/HeroSlidesManager";
import { AboutManager } from "./components/admin/AboutManager";
import { PartnersManager } from "./components/admin/PartnersManager";
import GalleryManager from "./components/admin/GalleryManager";
import PartyAlbumManager from "./components/admin/PartyAlbumManager";
import { UserManagement } from "./components/admin/UserManagement";
import { PasswordManagement } from "./components/admin/PasswordManagement";
import { RadioManager } from "./components/admin/RadioManager";

// Scroll to top on route change
const ScrollToTop = () => {
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  }, [pathname]);

  return null;
};

const HomePage = () => (
  <div className="page-transition relative">
    {/* Floating particles across entire page */}
    <div className="fixed inset-0 z-10 pointer-events-none">
      <FloatingDots count={window.innerWidth < 768 ? 120 : 260} primaryColor="orange" secondaryColor="green" />
    </div>
    <Navigation />
    <Hero />
    <SectionDivider />
    <HeroSliderSection />
    <SectionDivider />
    <Events />
    <SectionDivider />
    <Footer />
  </div>
);

const ContactPage = () => (
  <div className="page-transition">
    <Navigation />
    <Contact />
    <Footer />
  </div>
);

const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-black">
        <p className="text-white text-xl">Loading...</p>
      </div>
    );
  }

  return isAuthenticated ? children : <Navigate to="/admin/login" />;
};

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <RadioProvider>
          <ScrollToTop />
          <div className="App">
            <Routes>
              <Route path="/" element={<HomePage />} />
              <Route path="/releases" element={<ReleasesPage />} />
              <Route path="/releases/:releaseId" element={<ReleaseDetailPage />} />
              <Route path="/artists" element={<ArtistsPage />} />
              <Route path="/artists/:artistId" element={<ArtistDetailPage />} />
              <Route path="/gallery" element={<GalleryLanding />} />
              <Route path="/gallery/party" element={<PartyGallery />} />
              <Route path="/gallery/party/:albumId" element={<PartyGallery />} />
              <Route path="/gallery/:category" element={<GalleryView />} />
              <Route path="/about" element={<AboutPage />} />
              <Route path="/contact" element={<ContactPage />} />
              <Route path="/admin/login" element={<AdminLogin />} />
              <Route
                path="/admin"
                element={
                  <ProtectedRoute>
                    <AdminLayout />
                  </ProtectedRoute>
                }
              >
                <Route index element={<Navigate to="/admin/dashboard" />} />
                <Route path="dashboard" element={<Dashboard />} />
                <Route path="hero-slides" element={<HeroSlidesManager />} />
                <Route path="releases" element={<ReleasesManager />} />
                <Route path="artists" element={<ArtistsManager />} />
                <Route path="events" element={<EventsManager />} />
                <Route path="gallery" element={<GalleryManager />} />
                <Route path="party-albums" element={<PartyAlbumManager />} />
                <Route path="radio" element={<RadioManager />} />
                <Route path="about" element={<AboutManager />} />
                <Route path="partners" element={<PartnersManager />} />
                <Route path="contact-settings" element={<ContactManager />} />
                <Route path="contacts" element={<ContactsViewer />} />
                <Route path="users" element={<UserManagement />} />
                <Route path="password" element={<PasswordManagement />} />
              </Route>
            </Routes>
            <Toaster />
          </div>
        </RadioProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;